import { createContext, useCallback, useContext, useMemo, useState, type PropsWithChildren } from "react";
import { CheckCircle2, X } from "lucide-react";

interface ToastItem { id: string; message: string; action?: { label: string; run: () => void } }
interface ToastApi { show: (message: string, action?: ToastItem["action"]) => void }

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: PropsWithChildren) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const remove = useCallback((id: string) => setItems((current) => current.filter((item) => item.id !== id)), []);
  const show = useCallback((message: string, action?: ToastItem["action"]) => {
    const item: ToastItem = action ? { id: crypto.randomUUID(), message, action } : { id: crypto.randomUUID(), message };
    setItems((current) => [...current.slice(-2), item]);
    window.setTimeout(() => remove(item.id), 8000);
  }, [remove]);
  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="true">
        {items.map((item) => (
          <div className="toast" key={item.id}>
            <CheckCircle2 size={17} aria-hidden="true" />
            <span>{item.message}</span>
            {item.action && <button onClick={() => { item.action?.run(); remove(item.id); }}>{item.action.label}</button>}
            <button className="icon-button compact" aria-label="Dismiss notification" onClick={() => remove(item.id)}><X size={15} /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error("ToastProvider is missing.");
  return value;
}
